/* ==========================================================================
   LSC - Collage Collection: cookie helper (created 16 Oct 2026, v6;
   v8 16 Oct 2026: one cookie for filters AND view; v9: per-tab view backup)
   assets/lsc-collage-collection-cookie.js

   The section remembers two things per shopper, both in ONE cookie,
   lsc_filters, holding a query string:

     filter.p.m.custom.skin_concern=Acne&sort_by=price-ascending&lsc_view=list
     \___________ saved filters and sort order ___________/ \_ chosen view _/

     filters + sort   written by assets/lsc-collage-collection-filters.js
     lsc_view         written by assets/lsc-collage-collection-view.js
                      (collage | list; missing = the section's default)

   v6-v7 kept the view in its own lsc_cc_view cookie; v8 moved it in here so
   all of the shopper's collection preferences live in one place. v9 adds a
   per-tab backup of the view in sessionStorage (key lsc_view), used only
   when the cookie has none, for places the cookie can't be written (the
   theme editor preview, declined cookies); see "per-tab backup" below. The
   lsc_view key can never clash with a real filter: Shopify filter params
   all start with "filter.", and the filter script only ever applies keys
   its own form offers.

   Loaded by sections/lsc-collage-collection.liquid before the filter and
   view scripts (deferred scripts run in document order). The inline early
   script (snippets/lsc-collage-collection-early.liquid) reads the same
   cookie before the products are drawn; keep its parsing in step with this
   file.

   API: window.LscCcCookie
     getFilters()        -> saved filter / sort query string, or ''
     setFilters(query)   -> replace the saved filters, keeping the view
     getView()           -> 'collage', 'list' or '' (cookie, else tab backup)
     setView(view)       -> save the view, keeping the filters (and in the
                            tab backup)
     read(name), write(name, value), consentAllows()   low-level, as v6

   Writing a state with neither filters nor a view deletes the cookie.

   EXPIRY
   Written with an expiry in the year 9999 ("never"). Browsers cap cookie
   lifetimes (Chrome, Edge, Firefox: 400 days; Safari: 7 days for cookies
   set by page scripts), which is why the callers re-write the cookie on
   every visit: in practice it lasts while the shopper keeps coming back.

   CONSENT
   If Shopify's Customer Privacy API is on the page and says preference
   processing isn't allowed for this visitor, nothing is written (deleting
   is always allowed). Without the API (no consent banner) it is written.

   All cookies: path=/ (whole store), SameSite=Lax, Secure on https.
   ========================================================================== */
(function () {
  if (window.LscCcCookie) return; // loaded by another instance already

  var EXPIRES = 'Fri, 31 Dec 9999 23:59:59 GMT';
  var PREFS = 'lsc_filters'; // the one preferences cookie (see the header)
  var VIEW_KEY = 'lsc_view'; // its view entry
  var VIEWS = ['collage', 'list'];

  /** Shopify consent: may preference cookies be written for this visitor? */
  function consentAllows() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.preferencesProcessingAllowed === 'function') {
      try {
        return privacy.preferencesProcessingAllowed() !== false;
      } catch (e) {
        /* API present but not ready yet: allow */
      }
    }
    return true;
  }

  /** The decoded value of cookie `name`, or ''. */
  function read(name) {
    var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    if (!match) return '';
    try {
      return decodeURIComponent(match[1]);
    } catch (e) {
      return '';
    }
  }

  /** Save `value` under `name`; an empty value deletes the cookie. */
  function write(name, value) {
    var tail = '; path=/; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : '');
    if (!value) {
      document.cookie = name + '=; max-age=0' + tail;
      return;
    }
    if (!consentAllows()) return;
    document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + EXPIRES + tail;
  }

  // ---- v8: the preferences cookie --------------------------------------------

  /** Everything saved, as URLSearchParams. */
  function prefs() {
    return new URLSearchParams(read(PREFS));
  }

  /** Save `filters` (URLSearchParams without a view) plus `view`. The view
      goes last so the cookie reads filters first, as in v5-v7. */
  function save(filters, view) {
    filters.delete(VIEW_KEY);
    if (view) filters.set(VIEW_KEY, view);
    write(PREFS, filters.toString()); // empty = delete
  }

  // ---- v9: per-tab backup of the view -----------------------------------------
  // LSC - The cookie can't always be written: in the theme editor the
  // preview is a cross-site frame inside Shopify admin, where browsers
  // refuse SameSite=Lax cookies, and a shopper may have declined preference
  // cookies (consentAllows). Then page 2 opened in the default view (bug
  // found 16 Oct 2026). The view is therefore also kept in this tab's
  // sessionStorage under the same key: it lasts until the tab closes, works
  // in cross-site frames, and is only a fallback (the cookie wins when it
  // has a view). Every access is wrapped: storage can throw (private modes,
  // blocked site data).

  /** The view saved for this tab, or ''. */
  function sessionView() {
    try {
      var view = window.sessionStorage.getItem(VIEW_KEY);
      return VIEWS.indexOf(view) === -1 ? '' : view;
    } catch (e) {
      return '';
    }
  }

  /** Remember the view for this tab. */
  function saveSessionView(view) {
    try {
      window.sessionStorage.setItem(VIEW_KEY, view);
    } catch (e) {
      /* storage unavailable: the cookie is all there is */
    }
  }

  /** The saved view, or '' when none (or not a known view). v9: the
      cookie's, else this tab's backup. */
  function getView() {
    var view = prefs().get(VIEW_KEY);
    if (VIEWS.indexOf(view) !== -1) return view;
    return sessionView();
  }

  /** The saved filters and sort order, without the view. */
  function getFilters() {
    var params = prefs();
    params.delete(VIEW_KEY);
    return params.toString();
  }

  /** Replace the saved filters (a query string; '' clears them), keeping
      the view. Only the cookie's own view is carried over, so filter saves
      never copy the tab backup into the cookie. */
  function setFilters(query) {
    var view = prefs().get(VIEW_KEY);
    save(new URLSearchParams(query || ''), VIEWS.indexOf(view) === -1 ? '' : view);
  }

  /** Save the view (collage | list), keeping the filters. v9: also in this
      tab's backup (see above). */
  function setView(view) {
    if (VIEWS.indexOf(view) === -1) return;
    saveSessionView(view);
    save(new URLSearchParams(getFilters()), view);
  }

  window.LscCcCookie = {
    getFilters: getFilters,
    setFilters: setFilters,
    getView: getView,
    setView: setView,
    read: read,
    write: write,
    consentAllows: consentAllows
  };
})();
